import PropTypes from 'prop-types';
import { combineReducers } from 'redux';
import { globalizeSelectors } from '../Utils';
import {
  singleDataType,
  createSingleDataReducer,
  createArrayDataReducer,
  arrayDataType,
} from '../factories/ApiCall';
import type {
  DeleteInterventionPayload,
  FetchInterventionsPayload,
  MaintenanceAction,
  MaintenanceCase,
  MaintenanceListResponse,
  MaintenanceQtCommandPayload,
  MaintenanceQtCommandResponse,
  MaintenanceState,
  MaintenanceId,
  MaintenanceServiceDataResponse,
  SaveInterventionPayload,
  SetServiceDataPayload,
  UnseenMaintenanceCase,
} from '../types/Maintenance';

/*
 * action types
 */

const types = {
  FETCH_UNSEEN: 'MAINTENANCE/FETCH_UNSEEN',
  // Get list of maintenance actions
  MAINTENANCE_FETCH: 'MAINTENANCE/FETCH',
  // Add or edit maintenance case
  MAINTENANCE_SAVE: 'MAINTENANCE/SAVE',
  // Delete maintenance case
  MAINTENANCE_DELETE: 'MAINTENANCE/DELETE',
  // Set next service date for machine
  MAINTENANCE_SET_DATA: 'MAINTENANCE/SET_SERVICE_DATA',
  // Send log files (QT)
  MAINTENANCE_SEND_LOGS_QT: 'MAINTENANCE/SEND_LOGS_QT',
  // Browse log files (QT)
  MAINTENANCE_BROWSE_QT: 'MAINTENANCE/BROWSE_QT',
} as const;

const mountPath = 'maintenance'; // mount point in global state, must match root reducer

/*
 * action creators
 */

const actions = {
  fetchUnseen: (): MaintenanceAction => ({
    type: types.FETCH_UNSEEN,
  }),
  fetchInterventions: (
    machineid: number
  ): MaintenanceAction<FetchInterventionsPayload> => ({
    type: types.MAINTENANCE_FETCH,
    payload: { machinedbid: machineid },
  }),
  saveIntervention: (
    data: SaveInterventionPayload
  ): MaintenanceAction<SaveInterventionPayload> => ({
    type: types.MAINTENANCE_SAVE,
    payload: data,
  }),
  setServiceData: (
    machineid: number,
    data: Omit<SetServiceDataPayload, 'machinedbid'>
  ): MaintenanceAction<SetServiceDataPayload> => ({
    type: types.MAINTENANCE_SET_DATA,
    payload: { machinedbid: machineid, ...data },
  }),
  deleteIntervention: (
    maintenanceid: MaintenanceId
  ): MaintenanceAction<DeleteInterventionPayload> => ({
    type: types.MAINTENANCE_DELETE,
    payload: { maintenanceid },
  }),
  sendLogFiles: (
    siteid: number,
    maintenanceid: MaintenanceId
  ): MaintenanceAction<MaintenanceQtCommandPayload> => ({
    type: types.MAINTENANCE_SEND_LOGS_QT,
    payload: { siteid, maintenanceid },
  }),
  browseLogs: (
    maintenanceid: MaintenanceId
  ): MaintenanceAction<MaintenanceQtCommandPayload> => ({
    type: types.MAINTENANCE_BROWSE_QT,
    payload: { maintenanceid },
  }),
};

/*
 * state shape
 */
const propType = PropTypes.shape({
  save: singleDataType,
  delete: singleDataType,
  unseen: arrayDataType,
  set_service: singleDataType,
  send_logs: singleDataType,
  browse_logs: singleDataType,
  interventions: arrayDataType,
});

/*
 * reducer
 */
const reducer = combineReducers({
  save: createSingleDataReducer<MaintenanceCase, SaveInterventionPayload>(
    types.MAINTENANCE_SAVE
  ),
  unseen: createArrayDataReducer<UnseenMaintenanceCase, Record<string, never>>(
    types.FETCH_UNSEEN
  ),
  delete: createSingleDataReducer<MaintenanceCase, DeleteInterventionPayload>(
    types.MAINTENANCE_DELETE
  ),
  set_service: createSingleDataReducer<
    MaintenanceServiceDataResponse,
    SetServiceDataPayload
  >(types.MAINTENANCE_SET_DATA),
  interventions: createArrayDataReducer<
    MaintenanceListResponse[number],
    FetchInterventionsPayload
  >(types.MAINTENANCE_FETCH),
  send_logs: createSingleDataReducer<
    MaintenanceQtCommandResponse,
    MaintenanceQtCommandPayload
  >(types.MAINTENANCE_SEND_LOGS_QT),
  browse_logs: createSingleDataReducer<
    MaintenanceQtCommandResponse,
    MaintenanceQtCommandPayload
  >(types.MAINTENANCE_BROWSE_QT),
}) as (
  state: MaintenanceState | undefined,
  action: MaintenanceAction
) => MaintenanceState;

/*
 * selectors
 */
const localSelectors = {
  set_service: (state: MaintenanceState): MaintenanceState['set_service'] =>
    state.set_service,
  save: (state: MaintenanceState): MaintenanceState['save'] => state.save,
  interventions: (state: MaintenanceState): MaintenanceState['interventions'] =>
    state.interventions,
  unseen: (state: MaintenanceState): MaintenanceState['unseen'] => state.unseen,
};

const selectors = globalizeSelectors(localSelectors, mountPath);

export {
  types as actionTypes,
  actions as default,
  propType,
  selectors,
  reducer,
};
